// Building one play's round of cards (one session: 4–5 cards, about a minute) from a card set,
// and counting plays. The reward machine (src/arcade/machines) only decides how it looks.
import type { Pack } from '../../../shared/pack/schema'
import { rng } from '../rng'
import { scriptDecks } from '../kana'
import { lookup } from './cards'
import { explorer } from './cardSets/explorer'
import { kanaCapsules } from './cardSets/kanaCapsules'
import { numberRush } from './cardSets/numberRush'
import { speakUp } from './cardSets/speakUp'
import { playKey } from './progress'
import { isDue } from './srs'
import type { Item, CardSet, CardSetCtx, CardSetId } from './types'

export const CARD_SETS: CardSet[] = [kanaCapsules, speakUp, numberRush, explorer]
export const cardSetById = (id: CardSetId): CardSet => CARD_SETS.find((m) => m.id === id)!

/** The trip has language content (decks from a language kit); without, Learn hides and Explorer plays (B6). */
export const hasLanguage = (pack: Pack): boolean => pack.decks.length > 0

/**
 * The card sets this trip offers in the Arcade: the language card sets that have cards; on a trip
 * with no language kit, Explorer (provisional: the pilot's Arcade stays as it is).
 */
export function cardSetsFor(pack: Pack): CardSet[] {
  if (!hasLanguage(pack)) return [explorer]
  return CARD_SETS.filter((m) => m !== explorer && m.pool(pack).length > 0)
}

/**
 * A card set's name on the reward machine: "Kana Capsules" names the gashapon's capsules, so on
 * another machine it takes that machine's word ("Kana Chests"). Ids never change.
 */
export const cardSetTitle = (title: string, words: { many: string }, pack?: Pack) =>
  title
    .replace(/\bCapsules\b/, words.many.charAt(0).toUpperCase() + words.many.slice(1))
    // The reading machine names the kit's script (B7): "Kana" for Japanese, "Hangul" for Korean.
    // (A kit without a readName: its first script deck's title.)
    .replace(/\bKana\b/, (k) => (pack ? (pack.meta.readName ?? pack.decks.find((d) => d.id === scriptDecks(pack)[0])?.title ?? k) : k))

/**
 * A card set's shelf glyph: its own (123, ?!), or for the language machines the kit's first card
 * (B7: すみ from すみません, アイ from ア イ; 안녕 and 가나 on a Korean trip). Empty when the pack
 * has none.
 */
export function cardSetGlyph(set: CardSet, pack: Pack): string {
  if (set.theme.glyph) return set.theme.glyph
  const cards = set.pool(pack).slice(0, 2).map((id) => lookup(pack, id)?.card)
  const text = (c: (typeof cards)[number]) => (!c ? '' : c.kind === 'reading' ? c.script : c.kind === 'speak' ? c.target : (c.say ?? ''))
  const first = text(cards[0])
  // A single character (a script card) takes the next one too: アイ, 가나.
  return [...first].length === 1 ? first + [...text(cards[1])].slice(0, 1).join('') : [...first].slice(0, 2).join('')
}

function hash(s: string): number {
  let h = 2166136261
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619)
  return h >>> 0
}

/**
 * Cards for one capsule, deterministic per (machine, day, play number): due cards first
 * (lowest box, oldest), then up to `newPerSession` new eligible cards in pool order, then
 * a top-up of already-seen cards (weakest first). Order is shuffled. `isNew`: never answered,
 * so the session asks it with a "New" hint (WI-08; no meet card since 23 Oct). Its sound plays
 * first only when the sound is the question (soundBefore, K2).
 */
export function pickCards(m: CardSet, ctx: CardSetCtx, playNo: number): { id: string; isNew: boolean }[] {
  const rand = rng(hash(`${m.id}|${ctx.day}|${playNo}`))
  const cards = ctx.progress.cards
  const pool = m.pool(ctx.pack)
  const due = pool
    .filter((id) => isDue(cards[id], ctx.day))
    .sort((a, b) => cards[a]!.box - cards[b]!.box || (cards[a]!.due < cards[b]!.due ? -1 : 1))
  const fresh = pool.filter((id) => !cards[id] && m.eligible(ctx, id))
  const nNew = Math.min(m.newPerSession, fresh.length)
  const picked = [...due.slice(0, m.sessionSize - nNew), ...fresh.slice(0, nNew)]
  if (picked.length < m.sessionSize) {
    const seen = pool
      .filter((id) => cards[id] && !picked.includes(id))
      .sort((a, b) => cards[a]!.box - cards[b]!.box || rand() - 0.5)
    picked.push(...seen.slice(0, m.sessionSize - picked.length))
  }
  if (picked.length < m.sessionSize) picked.push(...fresh.slice(nNew, nNew + m.sessionSize - picked.length))
  for (let i = picked.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1))
    ;[picked[i], picked[j]] = [picked[j]!, picked[i]!]
  }
  return picked.map((id) => ({ id, isNew: !cards[id] }))
}

export function buildSession(m: CardSet, ctx: CardSetCtx, playNo: number): Item[] {
  const rand = rng(hash(`${m.id}|${ctx.day}|${playNo}|items`))
  return pickCards(m, ctx, playNo).flatMap(({ id, isNew }) => {
    const item = m.toItem(ctx, id, rand)
    return item ? [isNew ? { ...item, isNew } : item] : []
  })
}

/**
 * What a card plays before it's answered (K2). Only a sound that IS the question (`listen`: "what
 * did they say?"). A card that asks you to read something (a kana, a word, a price) keeps its
 * sound (`reveal.say`) for after the answer: heard first, it gives the answer away.
 */
export function soundBefore(item: Item): string | undefined {
  return item.listen || undefined
}

/** What the replay speaker plays, and whether it may yet: a reading card's sound only once answered (K2). */
export function replaySound(item: Item, answered: boolean): string | undefined {
  return soundBefore(item) ?? (answered ? item.reveal.say || undefined : undefined)
}

/** How many times a missed card comes back in one session (WI-08): it can't loop for ever. */
export const MAX_REQUEUE = 2

/**
 * After a wrong answer or a skip, the card comes back later in the same session: after two more
 * cards, or at the end, as a plain question (it has been met now). At most MAX_REQUEUE times per card.
 * Pure; returns the items unchanged when the card has come back enough.
 */
export function requeue(items: readonly Item[], index: number): Item[] {
  const item = items[index]
  if (!item) return [...items]
  const asked = items.filter((i) => i.cardId === item.cardId).length
  if (asked > MAX_REQUEUE) return [...items]
  const { isNew: _met, ...again } = item
  const at = Math.min(items.length, index + 3)
  return [...items.slice(0, at), again as Item, ...items.slice(at)]
}

/** Capsules of this machine started today: the play number that picks the next capsule's cards. */
export function playNumber(m: CardSet, ctx: CardSetCtx): number {
  return ctx.progress.used[playKey(ctx.day, m.id)] ?? 0
}

/** XP bonus for finishing a capsule (per right answer, plus a clean-sweep bonus). */
export function sessionBonus(right: number, total: number): number {
  return 5 + (right === total && total > 0 ? 5 : 0)
}
